import React, { useState, useEffect } from "react";
import { addMonths, addDays, format } from "date-fns";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Loader2, Check, Calendar, Repeat, ChevronsUpDown } from "lucide-react";

const categoriasReceita = [
  { value: "venda_graos", label: "Venda de Grãos", icon: "🌾" },
  { value: "venda_gado", label: "Venda de Gado", icon: "🐄" },
  { value: "venda_leite", label: "Venda de Leite", icon: "🥛" },
  { value: "venda_hortifruti", label: "Venda de Hortifruti", icon: "🥬" },
  { value: "arrendamento", label: "Arrendamento", icon: "🏠" },
  { value: "servicos", label: "Prestação de Serviços", icon: "🚜" },
  { value: "subsidios", label: "Subsídios/Incentivos", icon: "💰" },
  { value: "seguro", label: "Indenização de Seguro", icon: "🛡️" },
  { value: "financiamento", label: "Liberação de Financiamento", icon: "🏦" },
  { value: "outros", label: "Outros", icon: "📦" }
];

export default function ContaReceberForm({ conta, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  
  const [formData, setFormData] = useState({
    descricao: "",
    valor: "",
    data_vencimento: "",
    categoria: "",
    cliente: "",
    status: "pendente",
    recorrente: false,
    frequencia_recorrencia: "mensal",
    lembrete_dias: 3,
    forma_recebimento: "pix",
    observacoes: "",
    safra_id: "",
    parcela_atual: "",
    total_parcelas: "",
    nota_fiscal: ""
  });
  const [baixaData, setBaixaData] = useState({
    data_recebimento: "",
    juros: "",
    multa: "",
    desconto: "",
    conta_bancaria_id: ""
  });
  const [partnerOpen, setPartnerOpen] = useState(false);
  const { data: contratos = [] } = useQuery({
    queryKey: ['contratos_comercializacao', propriedadeId, formData.safra_id],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContratoComercializacao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });
  const [saving, setSaving] = useState(false);
  const { data: contasBancarias = [] } = useQuery({
    queryKey: ['contas_bancarias', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaBancaria.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId }),
    enabled: !!propriedadeId
  });
  
  const { data: parceiros = [] } = useQuery({
    queryKey: ['parceiros', propriedadeId],
    queryFn: () => agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId }),
    enabled: !!propriedadeId
  });

  useEffect(() => {
    if (conta) {
      setFormData({
        descricao: conta.descricao || "",
        valor: conta.valor?.toString() || "",
        data_vencimento: conta.data_vencimento || "",
        categoria: conta.categoria || "",
        cliente: conta.cliente || "",
        status: conta.status || "pendente",
        recorrente: conta.recorrente || false,
        frequencia_recorrencia: conta.frequencia_recorrencia || "mensal",
        lembrete_dias: conta.lembrete_dias || 3,
        forma_recebimento: conta.forma_recebimento || "pix",
        observacoes: conta.observacoes || "",
        safra_id: conta.safra_id || "",
        parcela_atual: conta.parcela_atual?.toString() || "",
        total_parcelas: conta.total_parcelas?.toString() || "",
        nota_fiscal: conta.nota_fiscal || ""
      });
      setBaixaData({
        data_recebimento: conta.data_recebimento || "",
        juros: conta.juros?.toString() || "",
        multa: conta.multa?.toString() || "",
        desconto: conta.desconto?.toString() || "",
        conta_bancaria_id: conta.conta_bancaria_id || ""
      });
    }
  }, [conta]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      ...formData,
      safra_id: formData.safra_id ? formData.safra_id : null,
      propriedade_id: propriedadeId,
      valor: parseFloat(formData.valor) || 0,
      lembrete_dias: parseInt(formData.lembrete_dias) || 3,
      parcela_atual: formData.parcela_atual ? parseInt(formData.parcela_atual) : null,
      total_parcelas: formData.total_parcelas ? parseInt(formData.total_parcelas) : null,
      contrato_id: formData.contrato_id || null,
      data_recebimento: baixaData.data_recebimento || null,
      juros: baixaData.juros ? parseFloat(baixaData.juros) : 0,
      multa: baixaData.multa ? parseFloat(baixaData.multa) : 0,
      desconto: baixaData.desconto ? parseFloat(baixaData.desconto) : 0,
      conta_bancaria_id: baixaData.conta_bancaria_id || null
    };
    
    if (conta) {
      await agrogestao.entities.ContaReceber.update(conta.id, data);
    } else {
      const parcelas = data.total_parcelas && data.total_parcelas > 1 ? data.total_parcelas : 1;
      if (parcelas === 1) {
        await agrogestao.entities.ContaReceber.create({ ...data, parcela_atual: data.parcela_atual || 1, total_parcelas: 1 });
      } else {
        const baseDate = new Date(formData.data_vencimento);
        const freq = formData.frequencia_recorrencia || 'mensal';
        const valorTotal = data.valor;
        const valorParcela = Math.round((valorTotal / parcelas) * 100) / 100;
        for (let i = 1; i <= parcelas; i++) {
          let due;
          if (freq === 'semanal') due = addDays(baseDate, 7 * (i - 1));
          else if (freq === 'quinzenal') due = addDays(baseDate, 15 * (i - 1));
          else if (freq === 'bimestral') due = addMonths(baseDate, 2 * (i - 1));
          else if (freq === 'trimestral') due = addMonths(baseDate, 3 * (i - 1));
          else if (freq === 'semestral') due = addMonths(baseDate, 6 * (i - 1));
          else if (freq === 'anual') due = addMonths(baseDate, 12 * (i - 1));
          else due = addMonths(baseDate, 1 * (i - 1));
          let v = valorParcela;
          if (i === parcelas) {
            v = Math.round((valorTotal - (valorParcela * (parcelas - 1))) * 100) / 100;
          }
          await agrogestao.entities.ContaReceber.create({
            ...data,
            descricao: `${formData.descricao} (${i}/${parcelas})`,
            valor: v,
            data_vencimento: format(due, 'yyyy-MM-dd'),
            parcela_atual: i,
            total_parcelas: parcelas
          });
        }
      }
    }
    
    setSaving(false);
    onSuccess();
  };
  
  const parceirosFiltrados = parceiros.filter(p => (p.tipo || "outro") === "cliente");
  const parceiroSelecionado = parceirosFiltrados.find((p) => {
    const nome = (p.nome_razao || p.razao_social || p.nome || "").trim();
    return nome && nome === formData.cliente;
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Descrição e Valor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Descrição *</Label>
          <Input
            placeholder="Ex: Venda soja safra 24/25..."
            value={formData.descricao}
            onChange={(e) => setFormData({...formData, descricao: e.target.value})}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Valor (R$) *</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.valor}
            onChange={(e) => setFormData({...formData, valor: e.target.value})}
            required
            className="text-lg font-semibold"
          />
        </div>
      </div>

      {/* Categoria e Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select 
            value={formData.categoria} 
            onValueChange={(v) => setFormData({...formData, categoria: v})}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a categoria" />
            </SelectTrigger>
            <SelectContent>
              {categoriasReceita.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  <span className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    {cat.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data Prevista *</Label>
          <Input
            type="date"
            value={formData.data_vencimento}
            onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
            required
          />
        </div>
      </div>

      {/* Cliente e Forma de Recebimento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Cliente/Comprador</Label>
          <Popover open={partnerOpen} onOpenChange={setPartnerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between bg-white font-normal"
              >
                {parceiroSelecionado
                  ? (parceiroSelecionado.nome_razao || parceiroSelecionado.razao_social || parceiroSelecionado.nome || parceiroSelecionado.cnpj_cpf)
                  : (formData.cliente || "Selecione um parceiro...")}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[420px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar parceiro..." />
                <CommandList>
                  <CommandEmpty>Nenhum parceiro encontrado.</CommandEmpty>
                  <CommandGroup>
                    {parceirosFiltrados.map((p) => {
                      const pNome = (p.nome_razao || p.razao_social || p.nome || "").trim();
                      const pDoc = (p.cnpj_cpf || "").trim();
                      const pEmail = (p.email || "").trim();
                      const label = pNome || pDoc || "Parceiro";
                      return (
                        <CommandItem
                          key={p.id}
                          value={label}
                          keywords={[pNome, pDoc, pEmail].filter(Boolean)}
                          onSelect={() => {
                            setFormData((prev) => ({ ...prev, cliente: label }));
                            setPartnerOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${label === formData.cliente ? "opacity-100" : "opacity-0"}`} />
                          <div className="flex flex-col">
                            <span>{label}</span>
                            {(pDoc || pEmail) && (
                              <span className="text-xs text-stone-500">{[pDoc, pEmail].filter(Boolean).join(" • ")}</span>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Forma de Recebimento</Label>
          <Select 
            value={formData.forma_recebimento} 
            onValueChange={(v) => setFormData({...formData, forma_recebimento: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pix">PIX</SelectItem>
              <SelectItem value="transferencia">Transferência</SelectItem>
              <SelectItem value="boleto">Boleto</SelectItem>
              <SelectItem value="deposito">Depósito</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="dinheiro">Dinheiro</SelectItem>
              <SelectItem value="cartao">Cartão</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Baixa da Receita */}
      {formData.status === 'recebido' && (
        <div className="mt-2 p-3 rounded-lg border border-stone-200 bg-stone-50">
          <p className="font-medium mb-2">Baixa do Contas a Receber</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-2">
              <Label>Data do Recebimento</Label>
              <Input type="date" value={baixaData.data_recebimento} onChange={(e) => setBaixaData({ ...baixaData, data_recebimento: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Juros</Label>
              <Input type="number" step="0.01" value={baixaData.juros} onChange={(e) => setBaixaData({ ...baixaData, juros: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Multa</Label>
              <Input type="number" step="0.01" value={baixaData.multa} onChange={(e) => setBaixaData({ ...baixaData, multa: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Desconto</Label>
              <Input type="number" step="0.01" value={baixaData.desconto} onChange={(e) => setBaixaData({ ...baixaData, desconto: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Conta Bancária</Label>
              <Select value={baixaData.conta_bancaria_id} onValueChange={(v) => setBaixaData({ ...baixaData, conta_bancaria_id: v === 'none' ? '' : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {contasBancarias.map(cb => (
                    <SelectItem key={cb.id} value={cb.id}>{cb.banco} • {cb.numero}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Parcelas */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Parcela Atual</Label>
          <Input
            type="number"
            placeholder="1"
            value={formData.parcela_atual}
            onChange={(e) => setFormData({...formData, parcela_atual: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Total de Parcelas</Label>
          <Input
            type="number"
            placeholder="12"
            value={formData.total_parcelas}
            onChange={(e) => setFormData({...formData, total_parcelas: e.target.value})}
          />
        </div>
      </div>

      {/* Recorrência */}
      <div className="p-4 bg-green-50 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Repeat className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium">Receita Recorrente</p>
              <p className="text-xs text-stone-500">Repetir automaticamente</p>
            </div>
          </div>
          <Switch
            checked={formData.recorrente}
            onCheckedChange={(checked) => setFormData({...formData, recorrente: checked})}
          />
        </div>
        
        {formData.recorrente && (
          <div className="space-y-2 pt-2 border-t border-green-200">
            <Label>Frequência</Label>
            <Select 
              value={formData.frequencia_recorrencia} 
              onValueChange={(v) => setFormData({...formData, frequencia_recorrencia: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semanal">Semanal</SelectItem>
                <SelectItem value="quinzenal">Quinzenal</SelectItem>
                <SelectItem value="mensal">Mensal</SelectItem>
                <SelectItem value="bimestral">Bimestral</SelectItem>
                <SelectItem value="trimestral">Trimestral</SelectItem>
                <SelectItem value="semestral">Semestral</SelectItem>
                <SelectItem value="anual">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Lembrete */}
      <div className="p-4 bg-amber-50 rounded-xl">
        <div className="flex items-center gap-3 mb-3">
          <Calendar className="w-5 h-5 text-amber-600" />
          <p className="font-medium">Lembrete de Recebimento</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-600">Lembrar</span>
          <Select 
            value={formData.lembrete_dias.toString()} 
            onValueChange={(v) => setFormData({...formData, lembrete_dias: parseInt(v)})}
          >
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 dia</SelectItem>
              <SelectItem value="2">2 dias</SelectItem>
              <SelectItem value="3">3 dias</SelectItem>
              <SelectItem value="5">5 dias</SelectItem>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="15">15 dias</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-stone-600">antes da data</span>
        </div>
      </div>

      {/* Safra e NF */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {safras.length > 0 && (
          <div className="space-y-2">
            <Label>Vincular a Safra</Label>
            <Select 
              value={formData.safra_id} 
              onValueChange={(v) => setFormData({...formData, safra_id: v === 'none' ? '' : v})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma safra" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                {safras.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <Label>Nota Fiscal</Label>
          <Input
            placeholder="Número da NF"
            value={formData.nota_fiscal}
            onChange={(e) => setFormData({...formData, nota_fiscal: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Contrato de Comercialização</Label>
          <Select 
            value={formData.contrato_id || 'none'}
            onValueChange={(v) => setFormData({ ...formData, contrato_id: v === 'none' ? null : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o contrato (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum</SelectItem>
              {contratos.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {(c.produto || 'Contrato')} • {c.cliente || ''} {c.quantidade && c.preco ? `• ${c.quantidade} @ ${c.preco}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Informações adicionais..."
          value={formData.observacoes}
          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button 
          type="submit"
          disabled={saving}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              {conta ? "Atualizar" : "Cadastrar"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export { categoriasReceita };
